/**
 * Files hung off a project, a task or a comment on a task.
 *
 * Its own file rather than another section of `lib/projects.ts` (which is past
 * seven hundred lines) and deliberately NOT part of `lib/mail.ts` either, even
 * though the two look alike from here: a mail attachment is staged before a
 * message exists and is spent by a send, while these rows ARE the record from
 * the moment the upload returns. The endpoints, the caps and the lifetimes are
 * all different, and the one thing they share - `safeFilename` and the blocked
 * extension list - is shared on the SERVER, where it can actually be enforced.
 *
 * Every export here is prefixed `crm`/`Crm` for a reason worth saying out loud:
 * `lib/mail.ts` already exports `uploadAttachment`, `deleteAttachment` and
 * `getAttachmentLimits`, and a second set under the same names is how a screen
 * ends up posting a project's file at the mail composer's staging table. The
 * prefix matches the Prisma model (`CrmAttachment`), so grepping either name
 * finds both halves.
 *
 * THERE IS NO `storageKey` IN THIS FILE, and there must never be one. The bucket
 * behind these objects is public-read - that is what lets tenant media load
 * without a signed URL - so a key that reached the browser would be an
 * unauthenticated download of a client's document that no later change takes
 * back. The server's `ATTACHMENT_PUBLIC_SELECT` is what keeps it out of the
 * replies; `crmAttachmentUrl` below is the only way to the bytes, and it names a
 * row id that the API resolves behind `view:crm`.
 */
import { api, API_BASE } from "./api.js";

/**
 * Which register `entityId` points into. The same three the server accepts
 * (`ATTACHMENT_ENTITIES`), written out rather than imported because nothing
 * crosses from the API package into this one.
 */
export type CrmAttachmentEntity = "project" | "task" | "taskcomment";

/** Who a file belongs to. The pair travels together everywhere. */
export interface CrmAttachmentOwner {
  entity: CrmAttachmentEntity;
  entityId: string;
}

/** One stored file, as a browser is allowed to see it - no key, by construction. */
export interface CrmAttachment {
  id: string;
  entity: string;
  entityId: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  /** Platform `User.id` of the uploader, or null on a file that was imported. */
  uploadedBy: string | null;
  /** ISO, as JSON carries it. */
  createdAt: string;
}

/**
 * What the picker may accept before it lets anybody try.
 *
 * Fetched rather than copied. Two versions of a limit drift, and the copy that
 * drifts is always the one making the promise to the operator - the server would
 * still refuse the file, just after the whole upload rather than before it.
 */
export interface CrmAttachmentLimits {
  maxBytes: number;
  maxFiles: number;
  /** Lowercase, with the leading dot: `[".exe", ".bat", ...]`. */
  blockedExtensions: string[];
}

export const getCrmAttachmentLimits = () =>
  api.get<CrmAttachmentLimits>("/admin/crm/attachments/limits");

/** One owner's files, oldest first - the order they were gathered in. */
export const listCrmAttachments = (owner: CrmAttachmentOwner) =>
  api.get<{ rows: CrmAttachment[] }>(`/admin/crm/attachments?${ownerQuery(owner)}`);

/**
 * Upload one file against an owner that already exists.
 *
 * The owner goes in the QUERY STRING, not in the form, and that is not a taste
 * question. `@fastify/multipart` fills `file.fields` from the parts it has
 * already parsed, so a text field appended AFTER the file simply is not there
 * when the handler reads it - a `FormData` built in the obvious order (file,
 * then entity, then entityId) would arrive looking like a request that named no
 * owner at all. Addressing information in the URL has no ordering to get wrong.
 */
export const uploadCrmAttachment = (owner: CrmAttachmentOwner, file: File, signal?: AbortSignal) => {
  const form = new FormData();
  form.append("file", file);
  return api.upload<CrmAttachment>(`/admin/crm/attachments?${ownerQuery(owner)}`, form, signal);
};

/** Remove one file. The bytes go with the row; there is no undo. */
export const deleteCrmAttachment = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/attachments/${encodeURIComponent(id)}`);

/**
 * Where the bytes come from.
 *
 * By ROW ID, through the API, and never a CDN address: see the file header. The
 * link is plain rather than a fetch, so the browser's own save dialog, progress
 * and retry do the work, and the session cookie travels because the API is
 * same-origin here (`lib/api.ts`).
 */
export const crmAttachmentUrl = (id: string) =>
  `${API_BASE}/admin/crm/attachments/${encodeURIComponent(id)}/download`;

/** `entity=...&entityId=...`, encoded once so every caller spells it the same way. */
function ownerQuery(owner: CrmAttachmentOwner): string {
  return new URLSearchParams({ entity: owner.entity, entityId: owner.entityId }).toString();
}
